/**
 * Typing indicator component to show when users are typing
 */
import React from 'react';
import { Box, Typography } from '@mui/material';

interface TypingUser {
  userId: string;
  username?: string;
  teamId: string;
  timestamp: Date;
}

interface TypingIndicatorProps {
  typingUsers: TypingUser[];
  teamColors?: {[key: string]: string};
  isTypingText?: string;
  areTypingText?: string;
  andText?: string;
  othersText?: string;
}

const TypingIndicator: React.FC<TypingIndicatorProps> = ({ 
  typingUsers, 
  teamColors = {},
  isTypingText = 'is typing...',
  areTypingText = 'are typing...',
  andText = 'and',
  othersText = 'others'
}) => {
  if (!typingUsers || typingUsers.length === 0) {
    return null;
  }
  
  // Group typing users by team
  const usersByTeam: {[key: string]: string[]} = {};
  
  typingUsers.forEach(user => {
    if (!usersByTeam[user.teamId]) {
      usersByTeam[user.teamId] = [];
    }
    
    usersByTeam[user.teamId].push(user.username || 'Someone');
  });
  
  // Create typing indicator messages by team
  const typingMessages = Object.entries(usersByTeam).map(([teamId, users]) => {
    // Get color for this team or use default
    const bgColor = teamColors[teamId] || '#f0f0f0';
    
    // Format message based on number of users typing
    let message = '';
    if (users.length === 1) {
      message = `${users[0]} ${isTypingText}`;
    } else if (users.length === 2) {
      message = `${users[0]} ${andText} ${users[1]} ${areTypingText}`;
    } else {
      message = `${users[0]} ${andText} ${users.length - 1} ${othersText} ${areTypingText}`;
    }
    
    return { teamId, message, bgColor };
  });
  
  return (
    <Box sx={{ position: 'relative', mt: 1, mb: 2 }}>
      {typingMessages.map((item, index) => (
        <Box 
          key={item.teamId}
          sx={{ 
            display: 'flex',
            alignItems: 'center',
            backgroundColor: item.bgColor,
            borderRadius: '16px',
            p: 1,
            pl: 2,
            mb: 1,
            maxWidth: '80%',
            opacity: 0.8
          }}
        >
          <Typography variant="body2" sx={{ fontStyle: 'italic', mr: 1 }}>
            {item.message}
          </Typography>
          
          {/* Animated dots */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {[0, 1, 2].map((dot) => (
              <Box
                key={dot}
                component="span"
                sx={{
                  width: '4px',
                  height: '4px',
                  borderRadius: '50%',
                  bgcolor: 'text.secondary',
                  mx: 0.25,
                  animation: 'pulse 1s infinite',
                  animationDelay: `${dot * 0.2}s`,
                  '@keyframes pulse': {
                    '0%': {
                      transform: 'scale(1)',
                      opacity: 0.7,
                    },
                    '50%': {
                      transform: 'scale(1.5)',
                      opacity: 1,
                    },
                    '100%': {
                      transform: 'scale(1)',
                      opacity: 0.7,
                    },
                  },
                }}
              />
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default TypingIndicator; 